"use client";

import React from "react";
import { Leaf } from "lucide-react";

import Link from "next/link";
import Image from "next/image";
import { env } from "next-runtime-env";
import { Button } from "../ui/button";

export function FeaturesSection() {
  const DASHBOARD_URL =
    env("NEXT_PUBLIC_DASHBOARD_URL") || "https://cloud.getmac.io";

  return (
    <section>
      {/* Top illustration */}
      <div className="flex justify-center mb-12 md:-mb-24">
        <div className="relative w-full max-w-[600px] h-[300px] md:h-[450px] px-4">
          <Image
            src="/images/virtual-machines-light.svg"
            alt="Virtual Machines Illustration"
            width={600}
            height={450}
            quality={100}
            className="dark:hidden"
            priority
          />
          <Image
            src="/images/virtual-machines-dark.svg"
            alt="Virtual Machines Illustration Dark"
            width={600}
            height={450}
            quality={100}
            className="hidden dark:block"
            priority
          />
        </div>
      </div>

      {/* Section heading */}
      <header className="text-center space-y-4 mb-8 md:mb-16 px-4">
        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight max-w-5xl mx-auto">
          Runner-ready Mac infrastructure, built for CI/CD.
        </h2>
        <div className="text-muted-foreground text-base sm:text-lg md:text-xl lg:text-2xl max-w-4xl mx-auto space-y-1">
          <p>High-performance macOS runners for GitHub Actions. No setup.</p>
          <p>
            Launch full macOS VMs on demand for testing, debugging, and
            development.
          </p>
        </div>
      </header>

      {/* Feature cards grid */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-8 md:mb-12 px-4">
        {featureCards.map((feature, idx) => (
          <FeatureCard
            key={idx}
            title={feature.title}
            description={feature.description}
            imageSrcDark={feature.imageSrcDark}
            imageSrcLight={feature.imageSrcLight}
            imageAlt={feature.imageAlt}
            imageScale={feature.imageScale}
            showBorder={feature.showBorder}
          />
        ))}
      </div>

      {/* Big text below the grid */}
      <div className="text-center my-8 sm:my-12 md:my-16 px-4">
        <span className="block text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight">
          ...and we&apos;re just{" "}
          <span className="text-primary">getting started</span>!
        </span>
      </div>


    </section>
  );
}

const featureCards = [
  {
    title: "Owned hardware",
    description: (
      <div className="space-y-4 text-sm mt-2">
        <p>
          We run on our own Apple Silicon hardware in a{" "}
          <Link
            href="/infrastructure"
            className="text-primary hover:underline"
          >
            TIER III, ISO-certified data center
          </Link>{" "}
          with dedicated racks, networking, and full operational control.
        </p>
        <p className="flex items-center gap-2 text-green-600 dark:text-green-400 font-medium">
          <Leaf className="w-4 h-4 animate-pulse" />
          Data center powered by 100% renewable energy
        </p>
      </div>
    ),
    imageSrcLight: "/images/data-center-racks.png",
    imageSrcDark: "/images/data-center-racks.png",
    imageScale: 1.1,
    imageAlt: "Data Center Racks",
    showBorder: false,
  },
  {
    title: "Modern and intuitive interface",
    description: "Manage virtual machines with ease using our sleek dashboard.",
    imageSrcLight: "/images/virtual-machines-main-light.png",
    imageSrcDark: "/images/virtual-machines-main-dark.png",
    imageScale: 1.8,
    imageAlt: "Dashboard Interface",
  },
  {
    title: "Create VMs in less than a minute",
    description:
      "Easy to set up and start using right away. No complex configurations needed.",
    imageSrcLight: "/images/create-virtual-machine-dialog-light.png",
    imageSrcDark: "/images/create-virtual-machine-dialog-dark.png",
    imageScale: 1.4,
    imageAlt: "Create VM Dialog",
  },
  {
    title: "Integrated Remote Terminal",
    description: "Easily access VMs via built-in remote terminal.",
    imageSrcLight: "/images/virtual-machine-terminal-light.png",
    imageSrcDark: "/images/virtual-machine-terminal-dark.png",
    imageScale: 1.4,
    imageAlt: "Terminal",
  },
  {
    title: "Integrated Remote Display",
    description: "Easily access VMs via built-in remote display.",
    imageSrcLight: "/images/virtual-machine-remote-display-light.png",
    imageSrcDark: "/images/virtual-machine-remote-display-dark.png",
    imageScale: 1.4,
    imageAlt: "Remote Display",
  },
  {
    title: "Automate with our REST API",
    description: "Programmatically manage VMs with our seamless REST API.",
    imageSrcLight: "/images/api-keys-light.png",
    imageSrcDark: "/images/api-keys-dark.png",
    imageScale: 1.4,
    imageAlt: "API Integration",
  },
  {
    title: "Direct native SSH access",
    description:
      "Connect to any virtual machine with your favorite SSH client.",
    imageSrcLight: "/images/ssh-keys-light.png",
    imageSrcDark: "/images/ssh-keys-dark.png",
    imageScale: 1.4,
    imageAlt: "Remote Display",
  },
  {
    title: "Pre-configured with all essentials",
    description:
      "Machine images come pre-installed with essential tools and software.",
    imageSrcLight: "/images/toolbox.png",
    imageSrcDark: "/images/toolbox.png",
    imageScale: 1,
    imageAlt: "Secure & Isolated",
    showBorder: false,
  },
  {
    title: "Machine types for every workload",
    description:
      "Multiple VM configuration options for every use case and budget.",
    imageSrcLight: "/images/apple-mac-mini-m4.png",
    imageSrcDark: "/images/apple-mac-mini-m4.png",
    imageAlt: "Machine Types",
    imageScale: 1,
    showBorder: false,
  },
  {
    title: "Built for developers, by developers",
    description: "Automate VM management with our Golang and Python SDKs.",
    imageSrcLight: "/images/gopher.png",
    imageSrcDark: "/images/gopher.png",
    imageScale: 1.2,
    imageAlt: "Secure & Isolated",
    showBorder: false,
  },
];

function FeatureCard({
  title,
  description,
  imageSrcDark,
  imageSrcLight,
  imageAlt,
  href,
  showBorder = true,
  imageScale = 1.4,
}: {
  title: string;
  description: React.ReactNode;
  imageSrcDark: string;
  imageSrcLight: string;
  imageAlt: string;
  href?: string;
  showBorder?: boolean;
  imageScale?: number;
}) {
  const borderClass = showBorder
    ? "border border-gray-100 dark:border-gray-800"
    : "";

  const CardContent = (
    <div className="group flex flex-col border relative transition-all hover:-translate-y-1.5 hover:z-10 hover:shadow-lg hover:border-primary bg-primary/20 dark:bg-primary px-0 py-0 overflow-hidden min-h-[300px] sm:min-h-[350px] md:min-h-[400px] max-h-[300px] sm:max-h-[350px] md:max-h-[400px] rounded-lg">
      {/* Dot grid background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, #ffffffff 1px, transparent 1px)",
          backgroundSize: "8px 8px",
          backgroundPosition: "0 0, 6px 6px",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none dark:block hidden"
        style={{
          backgroundImage: "radial-gradient(circle, #444 1px, transparent 1px)",
          backgroundSize: "8px 8px",
          backgroundPosition: "0 0, 6px 6px",
        }}
      />
      {/* Gradient overlay at top */}
      <div className="absolute inset-x-0 top-0 h-[200px] bg-gradient-to-b from-white via-white/100 to-transparent dark:from-gray-900 dark:via-gray-900/80 dark:to-transparent pointer-events-none" />
      <div className="relative flex flex-col flex-1">
        <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 pb-2 relative min-h-[8rem] sm:min-h-[9rem] md:min-h-[10rem]">
          <div className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 tracking-tight text-gray-800 dark:text-gray-100">
            {title}
          </div>
          <div className="text-sm sm:text-base md:text-md mb-4 font-medium tracking-tight leading-snug text-gray-600 dark:text-gray-300">
            {description}
          </div>
        </div>
        <div
          className={`relative origin-top-left overflow-hidden mt-auto transform translate-x-4 sm:translate-x-6 md:translate-x-8 transition-transform duration-300 translate-y-[0.5rem] sm:translate-y-[1rem] group-hover:translate-y-[-0.5rem] sm:group-hover:translate-y-[-1rem] flex items-start scale-[${imageScale}]`}
        >
          <Image
            alt={imageAlt}
            src={imageSrcLight}
            width={600}
            height={300}
            quality={100}
            className={`${borderClass} rounded-md dark:hidden block`}
            style={{ color: "transparent" }}
            loading="lazy"
          />
          <Image
            alt={imageAlt}
            src={imageSrcDark}
            width={600}
            height={300}
            quality={100}
            className={`${borderClass} rounded-md hidden dark:block`}
            style={{ color: "transparent" }}
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );

  return href ? (
    <Link href={href} className="block">
      {CardContent}
    </Link>
  ) : (
    CardContent
  );
}
